/**
 * Tracks "Page Engagement Summary V.1" events for Mixpanel
 *
 * Exactly ONE event per pageview, fired when the visit ends:
 *   - pagehide (navigation away / tab close), or
 *   - 30 seconds of user inactivity (also evaluated when a hidden tab
 *     becomes visible again, since browsers suspend background timers).
 *     A tab opened in the background isn't "inactive" — the idle clock only
 *     starts once the tab has actually been seen.
 *
 * If Mixpanel is not available when an inactivity exit triggers (OneTrust
 * consent not yet given, library still loading), the visit stays armed and the
 * check retries, so consent granted mid-visit still produces the event.
 *
 * Properties:
 *   - max_scroll_pct: % of the page's full height that entered the viewport (0-100,
 *     unbucketed). Derived at fire time against the final document height, so
 *     client-side-rendered pages (Algolia results) don't inflate it.
 *   - max_scroll_y_px / page_height_px / initial_viewport_height_px: raw inputs so
 *     the booleans below can be re-derived with different thresholds in queries
 *   - time_on_page_seconds: active time only — excludes time the tab was hidden
 *     and the trailing idle gap before an inactivity exit
 *   - time_to_max_scroll_seconds: seconds from page load to the deepest USER scroll
 *   - page_type: homepage / pdp / category / search / cart / other
 *   - scrolled_past_fold: deliberate scroll past the initial viewport (see
 *     PAST_FOLD_VIEWPORT_RATIO); only user-initiated scrolling counts, so browser
 *     scroll restoration and #anchor jumps don't fabricate engagement
 *   - reached_content_end: viewport reached the bottom of the main content area
 *     (footer excluded); derived at exit against the final layout so client-side
 *     rendering can't mark a barely-scrolled visit as complete
 *   - exit_trigger: 'pagehide' | 'inactivity' — which exit condition fired
 *   - Page: the LANDING url (matches Page View V.1; Algolia facet clicks mutate
 *     the url via pushState, so the exit-time href would skew attribution)
 *
 * This replaces the removed Visit Summary / Scroll Depth trackers (deleted in
 * 37ea86d7), which fired an event on every focus/blur/visibility/navigation
 * transition plus one per scroll threshold and were pulled for event volume.
 * The single-exit-event design here is what keeps volume at one event per pageview.
 */

(function () {
  'use strict';

  // How long without user input counts as "inactive" and ends the visit.
  const INACTIVITY_TIMEOUT_MS = 30 * 1000;

  // Ignore activity signals closer together than this (mousemove etc. fire constantly).
  const ACTIVITY_THROTTLE_MS = 1000;

  // A scroll within this window after a scroll-capable input counts as
  // user-driven (covers touch momentum). Beyond it, scrolls are treated as
  // programmatic (layout shifts, scrollIntoView) and only update content-seen
  // metrics — a permanent "has interacted" latch would let a keystroke minutes
  // earlier qualify an Algolia layout-shift scroll as user engagement.
  const USER_SCROLL_WINDOW_MS = 3000;

  // Scroll metrics are sampled at most this often; the reads force layout, so
  // per-frame sampling can cause jank when other scroll handlers dirty styles.
  const SCROLL_MEASURE_INTERVAL_MS = 200;

  // Scrolling at least this fraction of the initial viewport height counts as
  // "past the fold" — deliberate exploration rather than scroll jitter.
  const PAST_FOLD_VIEWPORT_RATIO = 0.25;

  // Viewport bottom within this distance of the main content's end counts as reaching it.
  const CONTENT_END_TOLERANCE_PX = 100;

  const TEMPLATE_PAGE_TYPES = {
    index: 'homepage',
    product: 'pdp',
    collection: 'category',
    search: 'search',
    cart: 'cart',
  };

  let hasFired = false;
  let startTs = 0;
  let landingHref = '';
  let pageType = 'other';
  let initialViewportHeight = 0;
  let contentEndEl = null;
  let everVisible = false; // background-opened tabs haven't been "visited" until first shown

  let maxViewportBottom = 0;
  let maxScrollY = 0;
  let timeToMaxScrollMs = 0;
  let lastDocumentHeight = 0;

  let activeMs = 0;
  let segmentStart = null; // start of the current visible-time segment, null while hidden
  let lastActivityTs = 0;
  let lastInteractionTs = -Infinity;
  let lastScrollMeasureTs = 0;

  // Monotonic clock: Date.now() can step with NTP corrections, producing
  // negative durations or spurious inactivity exits.
  function nowMs() {
    return window.performance && performance.now ? performance.now() : Date.now();
  }

  /**
   * Resolves the page type once at init. Primary source is the Liquid template
   * name (window.Shopify.template_name, set by a dedicated inline block in
   * theme.liquid just above this script's tag). The URL fallback covers deploy
   * skew: this asset can land on a theme whose theme.liquid predates that
   * block, and without the fallback every such pageview reports 'other'
   * (observed in QA: real PDP events arriving as page_type 'other').
   */
  function resolvePageType() {
    const templateName = (window.Shopify && window.Shopify.template_name) || '';
    if (templateName) {
      return TEMPLATE_PAGE_TYPES[templateName] || 'other';
    }

    const pathname = window.location.pathname;
    if (/\/products\//.test(pathname)) return 'pdp';
    if (/\/collections\//.test(pathname)) return 'category';
    if (/\/search\/?$/.test(pathname)) return 'search';
    if (/\/cart\/?$/.test(pathname)) return 'cart';
    if (/^\/([a-z]{2}(-[a-z]{2})?)?\/?$/i.test(pathname)) return 'homepage';
    return 'other';
  }

  /**
   * Updates scroll metrics from the current scroll position.
   * Deliberately NOT called on load: on pages that render content client-side
   * (e.g. Algolia collection/search results) the document height at script init
   * is far smaller than the final page. For the same reason max_scroll_pct is
   * derived only at fire time, from maxViewportBottom against the final height —
   * locking in a percentage mid-render would inflate it permanently.
   *
   * fromUserScroll gates the engagement signals (maxScrollY, timing): browser
   * scroll restoration on reload/back and #anchor jumps fire scroll events too,
   * and must not fabricate scrolled_past_fold / time_to_max_scroll values.
   */
  function measureScroll(fromUserScroll) {
    const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    lastDocumentHeight = Math.max(document.documentElement.scrollHeight, viewportHeight);

    const viewportBottom = scrollY + viewportHeight;
    if (viewportBottom > maxViewportBottom) {
      maxViewportBottom = viewportBottom;
    }

    if (fromUserScroll && scrollY > maxScrollY) {
      maxScrollY = scrollY;
      timeToMaxScrollMs = nowMs() - startTs;
    }

    // reached_content_end is derived at fire time (see fire()) — latching it
    // here would let a short pre-render skeleton mark the visit as complete.
  }

  // The timer chain is owned entirely by checkInactivity: it re-arms itself for
  // the remaining idle time, so activity handlers only update lastActivityTs.
  function checkInactivity() {
    if (hasFired) return;
    // A background-opened tab hasn't been seen yet — firing would emit a
    // phantom zero-engagement exit and block the real visit. Keep waiting;
    // the visibility handler restarts the idle clock on first view.
    if (!everVisible) {
      setTimeout(checkInactivity, INACTIVITY_TIMEOUT_MS);
      return;
    }
    const idleMs = nowMs() - lastActivityTs;
    if (idleMs >= INACTIVITY_TIMEOUT_MS) {
      // fire() declines while Mixpanel is unavailable (consent pending) — keep
      // checking so the visit is still reported if consent arrives later.
      if (!fire('inactivity')) {
        setTimeout(checkInactivity, INACTIVITY_TIMEOUT_MS);
      }
    } else {
      setTimeout(checkInactivity, INACTIVITY_TIMEOUT_MS - idleMs);
    }
  }

  function markActivity() {
    if (hasFired) return;
    const now = nowMs();
    if (now - lastActivityTs < ACTIVITY_THROTTLE_MS) return;
    lastActivityTs = now;
  }

  // Inputs that can cause scrolling (wheel/touch/key, and scrollbar-drag
  // mousedown where the browser delivers it) qualify nearby scroll events as
  // user-driven, distinguishing them from programmatic ones.
  function handleInteraction() {
    if (hasFired) return;
    lastInteractionTs = nowMs();
    markActivity();
  }

  function handleScroll() {
    if (hasFired) return;
    markActivity();
    const now = nowMs();
    if (now - lastScrollMeasureTs < SCROLL_MEASURE_INTERVAL_MS) return;
    lastScrollMeasureTs = now;
    measureScroll(now - lastInteractionTs <= USER_SCROLL_WINDOW_MS);
  }

  function handleVisibilityChange() {
    if (hasFired) return;
    const now = nowMs();

    if (document.visibilityState === 'hidden') {
      // Pause active-time accrual; the inactivity check keeps running so an
      // abandoned hidden tab still gets its exit event.
      if (segmentStart !== null) {
        activeMs += now - segmentStart;
        segmentStart = null;
      }
      return;
    }

    // First actual view of a tab opened in the background: start the visit
    // clock now — "settling inactivity" here would report a phantom exit for
    // a page the user only just saw.
    if (!everVisible) {
      everVisible = true;
      segmentStart = now;
      lastActivityTs = now;
      // time_to_max_scroll should measure from when the page could first be
      // seen, not from when the background tab was created.
      startTs = now;
      return;
    }

    // Tab became visible again. iOS suspends background timers, so the
    // inactivity deadline may have passed unseen — settle it now. If the event
    // can't send yet (consent pending), fall through and keep the visit alive.
    if (now - lastActivityTs >= INACTIVITY_TIMEOUT_MS && fire('inactivity')) {
      return;
    }

    segmentStart = now;
    lastActivityTs = now;
  }

  /**
   * Builds and sends the summary event. Returns true when the visit is
   * concluded (event sent, or bot), false when it must stay armed because
   * Mixpanel cannot accept the event yet.
   */
  function fire(trigger) {
    if (hasFired) return true;

    // Bots never produce useful engagement — end tracking without an event.
    if (typeof isBot === 'function' && isBot()) {
      hasFired = true;
      return true;
    }

    // Consent gate (OneTrust C0002 — see theme.liquid): window.mixpanel does not
    // exist until consent, and right after consent it is a stub that queues
    // calls in memory until the CDN library loads. For an inactivity exit we
    // can wait, so require the real library (__loaded). On pagehide there is no
    // later chance — the stub queue is accepted best-effort (it still sends if
    // the page survives into the back/forward cache).
    const mixpanelUsable = typeof window.mixpanel !== 'undefined' && !!window.mixpanel.track;
    if (!mixpanelUsable || (trigger !== 'pagehide' && window.mixpanel.__loaded !== true)) {
      return false;
    }

    // Withdrawing consent triggers a reload (see OptanonWrapper): mixpanel is
    // still loaded during that reload's pagehide, so re-check the live groups
    // rather than sending one last event after the user opted out.
    if (typeof OnetrustActiveGroups === 'string' && OnetrustActiveGroups.indexOf('C0002') === -1) {
      return false;
    }

    hasFired = true;

    // Final snapshot — covers visitors who never scrolled (short pages report
    // 100% seen, long pages the initial-viewport share).
    measureScroll(false);

    if (segmentStart !== null) {
      // lastActivityTs is never behind segmentStart (both reset together when
      // the tab becomes visible), so an inactivity exit trims the trailing idle gap.
      const segmentEnd = trigger === 'inactivity' ? lastActivityTs : nowMs();
      activeMs += segmentEnd - segmentStart;
      segmentStart = null;
    }

    const maxScrollPct = lastDocumentHeight > 0 ? Math.min(100, (maxViewportBottom / lastDocumentHeight) * 100) : 100;

    // Derived at exit against the final layout: a pre-render Algolia skeleton
    // is short enough to put the content end inside the first viewport, and a
    // value latched then would mark a barely-scrolled visit as complete.
    // (If the page SHRANK mid-visit this can over-report — "viewport ever
    // extended past where content now ends" — same hazard the latch had.)
    let reachedContentEnd = false;
    if (contentEndEl) {
      const pageScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      const contentEndOffset = contentEndEl.getBoundingClientRect().bottom + pageScrollY;
      reachedContentEnd = maxViewportBottom >= contentEndOffset - CONTENT_END_TOLERANCE_PX;
    }

    try {
      mixpanel.track(
        'Page Engagement Summary V.1',
        {
          max_scroll_pct: Math.round(maxScrollPct),
          max_scroll_y_px: Math.round(maxScrollY),
          page_height_px: Math.round(lastDocumentHeight),
          initial_viewport_height_px: Math.round(initialViewportHeight),
          time_on_page_seconds: Math.round(activeMs / 100) / 10,
          time_to_max_scroll_seconds: Math.round(timeToMaxScrollMs / 100) / 10,
          page_type: pageType,
          scrolled_past_fold:
            initialViewportHeight > 0 && maxScrollY >= initialViewportHeight * PAST_FOLD_VIEWPORT_RATIO,
          reached_content_end: reachedContentEnd,
          exit_trigger: trigger,
          Page: landingHref,
          'User Agent': navigator.userAgent,
        },
        // The pagehide path races page teardown: bypass the batch queue and use a beacon
        { transport: 'sendBeacon', send_immediately: true },
      );
    } catch (error) {
      // Error tracking Page Engagement Summary
    }
    return true;
  }

  function init() {
    contentEndEl = document.getElementById('MainContent') || document.querySelector('main');
    if (!contentEndEl) {
      // Redirect-stub pages render no body (/cart, forbidden collections) —
      // tracking them would emit phantom zero-time, 100%-scroll events.
      return;
    }

    startTs = nowMs();
    lastActivityTs = startTs;
    landingHref = window.location.href;
    pageType = resolvePageType();
    initialViewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    segmentStart = document.visibilityState === 'hidden' ? null : startTs;
    everVisible = segmentStart !== null;

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', markActivity, { passive: true });
    document.addEventListener('mousemove', markActivity, { passive: true, capture: true });
    ['mousedown', 'keydown', 'touchstart', 'wheel'].forEach(function (type) {
      document.addEventListener(type, handleInteraction, { passive: true, capture: true });
    });

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // pagehide is the reliable end-of-page signal (fires for navigation, tab
    // close, and bfcache entry; beforeunload would also break bfcache eligibility)
    window.addEventListener('pagehide', function () {
      fire('pagehide');
    });

    setTimeout(checkInactivity, INACTIVITY_TIMEOUT_MS);
  }

  // Don't start the clock on speculative prerenders the user may never see
  if (document.prerendering) {
    document.addEventListener('prerenderingchange', init, { once: true });
  } else {
    init();
  }
})();
